import { Link } from 'react-router-dom';
import { Download, Github, Box, BadgeCheck, ArrowRight, Code2, ExternalLink, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';

// Same release-pinning pattern as the Revit page — when we cut a new
// `.rbz` release, the "/latest" suffix automatically resolves on GitHub.
const REPO_SLUG = 'thisisradas/patinamart';
const RELEASE_URL = `https://github.com/${REPO_SLUG}/releases/latest`;
const SOURCE_URL = `https://github.com/${REPO_SLUG}/tree/main/sketchup-plugin`;

export default function SketchUpDownloadPage() {
  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="sketchup-download-page">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3 inline-flex items-center gap-2">
            <Box className="w-3 h-3" /> PatinaMart for SketchUp
          </p>
          <h1 className="font-playfair text-4xl md:text-6xl font-bold text-foreground leading-tight mb-5">
            Browse reclaimed stock
            <br />
            <span className="text-accent">without leaving SketchUp.</span>
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl">
            The PatinaMart extension adds a Marketplace panel inside SketchUp so you can search
            by category, condition and location, see embodied-carbon savings on every listing,
            and send an enquiry to the supplier — all before you commit a single wall.
          </p>
        </div>

        <div className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/10 to-transparent p-7 mb-10">
          <div className="flex items-start gap-4 mb-5">
            <div className="w-11 h-11 rounded-lg bg-accent/15 border border-accent/40 flex items-center justify-center flex-shrink-0">
              <Download className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="font-playfair text-2xl font-bold text-foreground mb-1">Download the latest release</h2>
              <p className="text-sm text-muted-foreground">
                Free · SketchUp 2021 + supported · Windows, macOS, Linux
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <a href={RELEASE_URL} target="_blank" rel="noreferrer" data-testid="sketchup-download-cta">
              <Button className="btn-gold">
                <Download className="w-4 h-4 mr-2" />
                Download .rbz from GitHub
              </Button>
            </a>
            <a href={SOURCE_URL} target="_blank" rel="noreferrer" className="text-sm text-muted-foreground hover:text-accent inline-flex items-center gap-1.5">
              <Github className="w-4 h-4" /> View source
              <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>

        <h3 className="font-playfair text-2xl mb-4">Install in 60 seconds</h3>
        <ol className="space-y-3 mb-10 text-sm text-muted-foreground list-decimal list-inside">
          <li>Download <code className="text-accent">patinamart-sketchup-vX.Y.Z.rbz</code> from the link above.</li>
          <li>In SketchUp: <strong className="text-foreground">Window → Extension Manager → Install Extension…</strong></li>
          <li>Select the <code className="text-accent">.rbz</code> file you just downloaded.</li>
          <li>Restart SketchUp. The PatinaMart menu lives under <strong className="text-foreground">Extensions → PatinaMart</strong> and a toolbar button appears too.</li>
        </ol>

        <h3 className="font-playfair text-2xl mb-4">What you can do today</h3>
        <div className="grid sm:grid-cols-2 gap-4 mb-10">
          {[
            { t: 'Browse the marketplace', d: 'Search, filter by category, condition, location radius, and "has 3D model" — same data the website uses.' },
            { t: 'See embodied-carbon savings', d: 'Every listing carries an emerald CO₂e badge so you can compare against virgin alternatives at sourcing time.' },
            { t: 'Send enquiries', d: 'One click on any listing opens a modal pre-filled with your name, email and company. The supplier gets a real email.' },
            { t: 'Open the listing on the web', d: 'Need the full photo gallery, supplier storefront, or 3D viewer? "View on web" pops it open in your default browser.' },
          ].map((f) => (
            <div key={f.t} className="bg-card border border-border rounded-xl p-5">
              <div className="w-9 h-9 rounded-lg bg-accent/15 text-accent flex items-center justify-center mb-3">
                <BadgeCheck className="w-4 h-4" />
              </div>
              <h4 className="font-playfair text-lg mb-1">{f.t}</h4>
              <p className="text-sm text-muted-foreground">{f.d}</p>
            </div>
          ))}
        </div>

        <div className="rounded-xl border border-border bg-card p-6 mb-10">
          <h3 className="font-playfair text-xl mb-2 inline-flex items-center gap-2">
            <Code2 className="w-4 h-4 text-accent" /> What's coming in v1.1
          </h3>
          <p className="text-sm text-muted-foreground">
            One-click <strong className="text-foreground">DAE / Collada import</strong> straight into your active model.
            SketchUp doesn't natively read GLB (the format we ship to the Revit plugin), so we're adding a server-side
            converter that streams a Collada file built from the same procedural geometry. Until then, ask the supplier
            for an <code className="text-accent">.skp</code> attachment via the enquiry modal — most are happy to share.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-4 text-sm">
          <Link to="/marketplace" className="text-accent hover:underline inline-flex items-center gap-1">
            Browse the marketplace on the web <ArrowRight className="w-3 h-3" />
          </Link>
          <span className="text-muted-foreground">·</span>
          <Link to="/revit" className="text-muted-foreground hover:text-accent inline-flex items-center gap-1">
            Using Revit instead? <ArrowRight className="w-3 h-3" />
          </Link>
          <span className="text-muted-foreground">·</span>
          <a href="mailto:hello@patinamart.com" className="text-muted-foreground hover:text-accent inline-flex items-center gap-1">
            <Mail className="w-3 h-3" /> Bug reports + feature requests
          </a>
        </div>
      </div>
    </div>
  );
}
